// components/Payroll/PayStubView.tsx
import React from "react";
import { useParams } from "react-router-dom";
import { Typography, Paper, Box, Grid, Divider, Button } from "@mui/material";
import { Print, Download } from "@mui/icons-material";
import { usePayroll } from "../../contexts/PayrollContext";
import { useAuth } from "../../contexts/AuthContext";

const PayStubView: React.FC = () => {
  const { payrollRunId } = useParams<{ payrollRunId: string }>();
  const { loadEmployeePayStub } = usePayroll();
  const { user } = useAuth();

  // In a real app, you'd fetch the actual pay stub data
  // For now, we'll use mock data
  const payStub: any = user
    ? loadEmployeePayStub(user.username, payrollRunId || "")
    : null;

  const mockPayStub = {
    employeeName: `${user?.attributes?.given_name || "John"} ${
      user?.attributes?.family_name || "Doe"
    }`,
    employeeId: user?.username || "EMP001",
    payPeriod: "2024-01-01 to 2024-01-15",
    payDate: "2024-01-15",
    grossPay: 2500.0,
    netPay: 1950.5,
    taxes: {
      federal: 375.0,
      state: 125.5,
      socialSecurity: 155.0,
      medicare: 36.25,
    },
    hoursWorked: 80,
    payRate: 31.25,
    deductions: 0,
  };

  const currentPayStub = payStub
    ? {
        employeeName: `${user?.attributes?.given_name || "John"} ${
          user?.attributes?.family_name || "Doe"
        }`,
        employeeId: user?.username || "EMP001",
        payPeriod: "Current Period",
        payDate: new Date().toISOString().split("T")[0],
        grossPay: payStub.grossPay,
        netPay: payStub.netPay,
        taxes: payStub.taxes,
        hoursWorked: payStub.hoursWorked,
        payRate: payStub.payRate,
        deductions: payStub.deductions,
      }
    : mockPayStub;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // In a real app, this would generate a PDF
    alert("PDF download functionality would be implemented here");
  };

  return (
    <div className="p-6">
      <Box className="flex justify-between items-center mb-6">
        <Typography variant="h4">Pay Stub</Typography>
        <Box className="space-x-2">
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownload}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      <Paper className="p-6">
        {/* Header */}
        <Box className="text-center mb-6">
          <Typography variant="h5" gutterBottom>
            Almond Tree Care
          </Typography>
          <Typography variant="h6" color="primary">
            Earnings Statement
          </Typography>
        </Box>

        <Divider className="my-4" />

        {/* Employee and Pay Period Info */}
        <Grid container spacing={4} className="mb-4">
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Employee Name
            </Typography>
            <Typography variant="body1">
              {currentPayStub.employeeName}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Employee ID
            </Typography>
            <Typography variant="body1">{currentPayStub.employeeId}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Pay Period
            </Typography>
            <Typography variant="body1">{currentPayStub.payPeriod}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Pay Date
            </Typography>
            <Typography variant="body1">{currentPayStub.payDate}</Typography>
          </Grid>
        </Grid>

        <Divider className="my-4" />

        {/* Earnings */}
        <Box className="mb-4">
          <Typography variant="h6" gutterBottom>
            Earnings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography>Regular Hours</Typography>
            </Grid>
            <Grid item xs={6} className="text-right">
              <Typography>{currentPayStub.hoursWorked} hours</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>Hourly Rate</Typography>
            </Grid>
            <Grid item xs={6} className="text-right">
              <Typography>
                {formatCurrency(currentPayStub.payRate)}/hr
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" fontWeight="bold">
                Gross Pay
              </Typography>
            </Grid>
            <Grid item xs={6} className="text-right">
              <Typography variant="subtitle1" fontWeight="bold">
                {formatCurrency(currentPayStub.grossPay)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider className="my-4" />

        {/* Deductions */}
        <Box className="mb-4">
          <Typography variant="h6" gutterBottom>
            Deductions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography>Federal Income Tax</Typography>
            </Grid>
            <Grid item xs={6} className="text-right">
              <Typography>
                {formatCurrency(currentPayStub.taxes.federal)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>State Income Tax</Typography>
            </Grid>
            <Grid item xs={6} className="text-right">
              <Typography>
                {formatCurrency(currentPayStub.taxes.state)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>Social Security</Typography>
            </Grid>
            <Grid item xs={6} className="text-right">
              <Typography>
                {formatCurrency(currentPayStub.taxes.socialSecurity)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>Medicare</Typography>
            </Grid>
            <Grid item xs={6} className="text-right">
              <Typography>
                {formatCurrency(currentPayStub.taxes.medicare)}
              </Typography>
            </Grid>
            {currentPayStub.deductions > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography>Other Deductions</Typography>
                </Grid>
                <Grid item xs={6} className="text-right">
                  <Typography>
                    {formatCurrency(currentPayStub.deductions)}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        <Divider className="my-4" />

        {/* Net Pay */}
        <Box className="text-center">
          <Typography variant="h5" gutterBottom color="primary">
            Net Pay
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {formatCurrency(currentPayStub.netPay)}
          </Typography>
        </Box>

        <Divider className="my-4" />

        {/* Footer */}
        <Box className="text-center mt-6">
          <Typography variant="body2" color="textSecondary">
            Thank you for your hard work!
          </Typography>
          <Typography variant="caption" color="textSecondary">
            This is an automated pay stub. Please contact HR with any questions.
          </Typography>
        </Box>
      </Paper>
    </div>
  );
};

export default PayStubView;
